import React, { useEffect, useState } from "react";
import { hasSchoolJourney } from "./journeyRegistry";
import { loadSchoolJourney } from "./loadSchoolJourney";
import CatchmentVisual from "./CatchmentVisual";
import CatchmentExample from "./CatchmentExample";
import "./schoolJourney.css";

export default function SchoolJourneyPage({ school, onBack, onOpenCatchmentMap }) {
  const [journey, setJourney] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!school || !hasSchoolJourney(school)) {
      setJourney(null);
      setLoadState("missing");
      return undefined;
    }

    setLoadState("loading");
    setJourney(null);
    loadSchoolJourney(school)
      .then(({ journey: next }) => {
        if (cancelled) return;
        if (!next) {
          setJourney(null);
          setLoadState("missing");
          return;
        }
        setJourney(next);
        setActiveId(next.steps?.[0]?.id || "");
        setLoadState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setJourney(null);
        setLoadState("missing");
      });

    return () => {
      cancelled = true;
    };
  }, [school]);

  useEffect(() => {
    if (!journey?.steps?.length) return undefined;
    setActiveId(journey.steps[0].id);

    const nodes = journey.steps
      .map((step) => document.getElementById(`sj-step-${step.id}`))
      .filter(Boolean);

    if (!nodes.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id.replace(/^sj-step-/, ""));
        }
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0.15, 0.4, 0.7] }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [journey]);

  if (!school || loadState === "missing" || (loadState === "ready" && !journey)) {
    return (
      <div className="sj-page">
        <div className="sj-shell">
          <button type="button" className="sj-back" onClick={onBack}>
            ← Back
          </button>
          <p className="sj-missing">This school guide is not ready yet.</p>
        </div>
      </div>
    );
  }

  if (loadState === "loading" || !journey) {
    return (
      <div className="sj-page">
        <div className="sj-shell">
          <button type="button" className="sj-back" onClick={onBack}>
            ← Back
          </button>
          <p className="sj-missing">Loading school guide…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sj-page">
      <div className="sj-shell">
        <header className="sj-hero">
          <div className="sj-top-actions">
            <button type="button" className="sj-back" onClick={onBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
              Back to school
            </button>
            <button type="button" className="sj-close" onClick={onBack} aria-label="Close guide" title="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <p className="sj-kicker">{journey.kicker}</p>
          <h1 className="sj-title">{journey.title}</h1>
          <p className="sj-school">{journey.schoolName}</p>
          <p className="sj-lead">{journey.lead}</p>

          {journey.heroStats?.length ? (
            <div className="sj-hero-stats" aria-label="School snapshot">
              {journey.heroStats.map((stat) => (
                <div key={stat.label} className="sj-hero-stat">
                  <span className="sj-hero-stat-value">{stat.value}</span>
                  <span className="sj-hero-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </header>

        <div className="sj-layout">
          <nav className="sj-toc" aria-label="Guide steps">
            <p className="sj-toc-label">In order</p>
            <ol className="sj-toc-list">
              {journey.steps.map((step) => (
                <li key={step.id}>
                  <a
                    href={`#sj-step-${step.id}`}
                    className={`sj-toc-link${activeId === step.id ? " is-on" : ""}`}
                    onClick={() => setActiveId(step.id)}
                  >
                    <span className="sj-toc-title">{step.title}</span>
                    <span className="sj-toc-summary">{step.summary}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="sj-main">
            {journey.steps.map((step, index) => (
              <section
                key={step.id}
                id={`sj-step-${step.id}`}
                className="sj-step"
                aria-labelledby={`sj-step-title-${step.id}`}
              >
                <div className="sj-step-index" aria-hidden>
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="sj-step-body">
                  <h2 id={`sj-step-title-${step.id}`} className="sj-step-title">
                    {step.title}
                  </h2>
                  <p className="sj-step-summary">{step.summary}</p>
                  {step.paragraphs?.map((text) => (
                    <p key={text.slice(0, 48)} className="sj-copy">
                      {text}
                    </p>
                  ))}
                  {step.showCatchmentVisual && journey.catchmentVisual ? (
                    <CatchmentVisual
                      schoolId={journey.schoolId}
                      visual={journey.catchmentVisual}
                      school={school}
                      onOpenMap={
                        onOpenCatchmentMap ? () => onOpenCatchmentMap(school) : undefined
                      }
                    />
                  ) : null}
                  {step.facts?.length ? (
                    <div className="sj-facts">
                      {step.facts.map((fact) => (
                        <div key={fact.label} className="sj-fact">
                          <strong>{fact.value}</strong>
                          <span>{fact.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {step.placeOrder?.length ? (
                    <ol className="sj-place-order">
                      {step.placeOrder.map((item, orderIndex) => (
                        <li key={`${item.title}-${orderIndex}`}>
                          <span className="sj-place-order-num">{orderIndex + 1}</span>
                          <span className="sj-place-order-copy">
                            <strong>{item.title}</strong>
                            <span>{item.detail}</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {step.points?.length ? (
                    <ul className="sj-points">
                      {step.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  ) : null}
                  {step.example ? <CatchmentExample example={step.example} /> : null}
                  {step.timeline?.length ? (
                    <ol className="sj-timeline">
                      {step.timeline.map((item) => (
                        <li key={`${item.when}-${item.what}`}>
                          <span className="sj-when">{item.when}</span>
                          <span className="sj-what">{item.what}</span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {step.nearby?.length ? (
                    <ul className="sj-nearby">
                      {step.nearby.map((item) => (
                        <li key={item.name}>
                          <strong>{item.name}</strong>
                          <span>{item.detail}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {step.schools?.length ? (
                    <ul className="sj-schools">
                      {step.schools.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </section>
            ))}

            <footer className="sj-sources">
              <h2 className="sj-sources-title">Check on the school site</h2>
              <p className="sj-copy">
                Dates and rules change by entry year. Use the school&apos;s own pages as the final
                source before you register or fill in the CAF.
              </p>
              <ul className="sj-source-list">
                {journey.sources.map((source) => (
                  <li key={source.href}>
                    <a href={source.href} target="_blank" rel="noreferrer">
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </footer>
          </div>
        </div>

        <div className="sj-bottom-actions">
          <button type="button" className="sj-back sj-back--bottom" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            Back to school
          </button>
        </div>
      </div>
    </div>
  );
}
